import { PasswordStrengthIndicator } from '@/components/auth/PasswordStrengthIndicator';
import { InputField } from '@/components/common/form-fields';
import { registrationService } from '@/services/registration.service';
import { userInvitationService } from '@/services/user-invitation.service';
import { SelfRegistrationRequest } from '@/types/auth.types';
import { generateRequestId, encryptAES } from '@/utils/crypto';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowRight,
  Briefcase,
  Building,
  CheckCircle,
  Eye,
  EyeOff,
  Lock,
  Mail,
  User
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';
import { isValidPhoneNumber } from 'react-phone-number-input';

const registrationSchema = yup.object().shape({
  email: yup.string().email('Invalid email address').required('Email is required'),
  password: yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/\d/, 'Password must contain at least one number')
    .required('Password is required'),
  confirm_password: yup.string()
    .oneOf([yup.ref('password')], 'Passwords must match')
    .required('Please confirm your password'),
  given_name: yup.string().required('First name is required'),
  family_name: yup.string().required('Last name is required'),
  mobile_number: yup.string().optional(),
  company: yup.string().optional(),
  job_title: yup.string().optional(),
  employee_id: yup.string().optional(),
  referral_code: yup.string().optional(),
});

type RegistrationFormData = {
  email: string;
  password: string;
  confirm_password: string;
  given_name: string;
  family_name: string;
  mobile_number?: string;
  company: string;
  job_title: string;
  employee_id: string;
  referral_code: string;
};

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get('token');

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<'weak' | 'medium' | 'strong' | 'very_strong'>('weak');
  const [emailAvailable, setEmailAvailable] = useState<boolean | null>(null);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [registrationAllowed, setRegistrationAllowed] = useState(true);
  const [verificationSent, setVerificationSent] = useState(false);

  // Invitation-specific state
  const [isInvitationMode, setIsInvitationMode] = useState(!!invitationToken);
  const [invitationData, setInvitationData] = useState<any>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<RegistrationFormData>({
    resolver: yupResolver(registrationSchema),
    context: { isInvitationMode },
    defaultValues: {
      email: '',
      password: '',
      confirm_password: '',
      given_name: '',
      family_name: '',
      mobile_number: '',
      company: '',
      job_title: '',
      employee_id: '',
      referral_code: '',
    }
  });

  const email = watch('email');
  const password = watch('password');

  // Cache Spline assets only for this page; OneSignal owns root scope.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker
      .register('/spline-sw.js', { scope: '/register' })
      .catch((err) => console.warn('[Spline SW] register failed:', err));
  }, []);

  // Verify invitation token if present
  useEffect(() => {
    const verifyInvitationToken = async () => {
      if (!invitationToken) {
        return;
      }

      setTokenError(null);

      try {
        const response = await userInvitationService.verifyInvitation(invitationToken);

        if (response.success && response.data) {
          // Check if invitation has valid data
          const invitationInfo = response.data.invitation || response.data;

          // Additional validation for invitation data
          if (!invitationInfo.email) {
            const errorMsg = 'Invalid invitation data: missing email';
            setTokenError(errorMsg);
            setIsInvitationMode(false);
            toast.error(errorMsg);
            return;
          }

          // Check if invitation is expired
          if (invitationInfo.expires_at) {
            const expiryDate = new Date(invitationInfo.expires_at);
            if (expiryDate < new Date()) {
              const errorMsg = 'This invitation has expired. Please request a new invitation.';
              setTokenError(errorMsg);
              setIsInvitationMode(false);
              toast.error(errorMsg);
              return;
            }
          }

          setInvitationData(invitationInfo);
          setIsInvitationMode(true);
          setRegistrationAllowed(true);
          setTokenError(null);
          
          // Pre-fill form with invitation data
          setTimeout(() => {
            if (invitationInfo.email) {
              setValue('email', invitationInfo.email);
              setEmailAvailable(true); // Invited emails are valid
            }
            if (invitationInfo.full_name) {
              const nameParts = invitationInfo.full_name.split(' ');
              if (nameParts.length >= 2) {
                setValue('given_name', nameParts[0]);
                setValue('family_name', nameParts.slice(1).join(' '));
              } else {
                setValue('given_name', invitationInfo.full_name);
              }
            }
            if (invitationInfo.given_name) setValue('given_name', invitationInfo.given_name);
            if (invitationInfo.family_name) setValue('family_name', invitationInfo.family_name);
            if (invitationInfo.employee_id) setValue('employee_id', invitationInfo.employee_id);
          }, 100);
          
          toast.success('Invitation verified successfully!');
        } else {
          // Handle API error response
          const errorMsg = response.error?.error_description || 
                          response.error?.message || 
                          response.description ||
                          'Invalid or expired invitation token';
          setTokenError(errorMsg);
          setIsInvitationMode(false);
          toast.error(errorMsg);
        }
      } catch (error: any) {
        console.error('Token verification failed:', error);

        const errorMessage = error?.response?.data?.error?.error_description ||
                            error?.response?.data?.error?.message ||
                            error?.response?.data?.error_description ||
                            error?.response?.data?.message ||
                            error?.message ||
                            'Failed to verify invitation token';
        setTokenError(errorMessage);
        setIsInvitationMode(false);
        toast.error(errorMessage);
      }
    };
    
    verifyInvitationToken();
  }, [invitationToken, setValue]);
  
  // Check if self-registration is enabled (only for non-invitation flow)
  useEffect(() => {
    if (isInvitationMode) return; // Skip for invitation flow
    
    const checkRegistration = async () => {
      try {
        const isEnabled = await registrationService.isSelfRegistrationEnabled();
        setRegistrationAllowed(isEnabled);
      } catch (error) {
        setRegistrationAllowed(false);
      }
    };
    checkRegistration();
  }, [isInvitationMode]);

  // Check email availability
  useEffect(() => {
    if (!email || !email.includes('@')) {
      setEmailAvailable(null);
      return;
    }

    const checkEmail = async () => {
      setCheckingEmail(true);
      try {
        const isAvailable = await registrationService.checkEmailAvailability(email);
        setEmailAvailable(isAvailable);
      } catch (error) {
        setEmailAvailable(null);
      } finally {
        setCheckingEmail(false);
      }
    };

    const debounceTimer = setTimeout(checkEmail, 500);
    return () => clearTimeout(debounceTimer);
  }, [email]);

  const registrationMutation = useMutation({
    mutationFn: (data: RegistrationFormData) => {
      // Generate request_id as AES encryption key
      const requestId = generateRequestId();

      // Check if mobile number is provided (not empty or just country code)
      const getMobileNumber = (value: string | undefined) => {
        if (!value || value.trim() === '') return null;
        const digitsOnly = value.replace(/\D/g, '');
        // If only country code (≤3 digits), treat as empty
        if (digitsOnly.length <= 3) return null;
        return value;
      };
      const mobileNumber = getMobileNumber(data.mobile_number);
      const hasMobileNumber = mobileNumber !== null;

      if (isInvitationMode && invitationToken) {
        // Invitation-based registration
        const encryptedPassword = encryptAES(data.password, requestId);

        return userInvitationService.completeRegistration({
          request_id: requestId,
          token: invitationToken,
          password: encryptedPassword,
          security_question: undefined,
          security_answer: undefined,
          profile_picture: undefined,
          additional_profile: {
            ...(hasMobileNumber && { mobile_number: mobileNumber }),
            company: data.company,
            job_title: data.job_title,
            employee_id: data.employee_id,
          },
        });
      } else {
        // Self-registration - encrypt email, password, confirm_password
        const encryptedEmail = encryptAES(data.email, requestId);
        const encryptedPassword = encryptAES(data.password, requestId);
        const encryptedConfirmPassword = encryptAES(data.confirm_password, requestId);

        const registrationData: SelfRegistrationRequest = {
          request_id: requestId,
          email: encryptedEmail,
          password: encryptedPassword,
          confirm_password: encryptedConfirmPassword,
          given_name: data.given_name,
          family_name: data.family_name,
          ...(hasMobileNumber && { mobile_number: mobileNumber }),
          additional_info: {
            company: data.company,
            job_title: data.job_title,
            employee_id: data.employee_id,
          },
          referral_code: data.referral_code,
        };
        return registrationService.selfRegister(registrationData);
      }
    },
    onSuccess: (result) => {
      if (isInvitationMode) {
        // Invitation registration is complete - redirect to login
        toast.success('Registration completed successfully! You can now log in with your credentials.');
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      } else {
        // Self-registration ALWAYS requires email verification (no tokens returned)
        setVerificationSent(true);
        toast.success(result.message || 'Registration successful! Please check your email to verify your account.');
      }
    },
    onError: (error: any) => {
      const errorCode = error?.response?.data?.error?.error;
      const errorMessage = error?.response?.data?.error?.error_description || error?.message || 'Registration failed';

      if (errorCode === 'employee_id_already_exists') {
        toast.error('Employee ID already exists');
        setError('employee_id', { message: 'Employee ID already exists' });
      } else if (errorMessage.includes('email') && errorMessage.includes('exists')) {
        setError('email', { message: 'This email is already registered' });
        setEmailAvailable(false);
      } else if (errorMessage.includes('self-registration')) {
        setRegistrationAllowed(false);
      } else {
        toast.error(errorMessage);
      }
    },
  });

  const onSubmit = (data: RegistrationFormData) => {
    if (passwordStrength === 'weak') {
      toast.error('Please choose a stronger password');
      return;
    }
    
    if (emailAvailable === false && !isInvitationMode) {
      setError('email', { message: 'This email is already registered' });
      return;
    }

    registrationMutation.mutate(data);
  };

  if (!registrationAllowed) {
    return (
      <div className="w-full">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900">Registration Not Available</h2>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div>
              <p className="text-sm text-yellow-800 mb-3">
                Self-registration is currently not available. Please contact your administrator to request an invitation.
              </p>
              <Link
                to="/login"
                className="text-sm text-primary-700 hover:text-primary-800 font-medium"
              >
                ← Back to login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (verificationSent) {
    return (
      <div className="w-full">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900">Check Your Email</h2>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
            <div>
              <p className="text-sm text-green-800 mb-3">
                We've sent a verification email to <strong>{watch('email')}</strong>. 
                Please check your inbox and click the verification link to complete your registration.
              </p>
              <p className="text-sm text-gray-600 mb-4">
                Didn't receive the email? Check your spam folder or{' '}
                <button
                  onClick={() => {
                    registrationService.resendVerification(watch('email'))
                      .then(() => toast.success('Verification email resent'))
                      .catch(() => toast.error('Failed to resend email. Please try again later.'));
                  }}
                  className="text-primary-700 hover:text-primary-800 font-medium"
                >
                  resend verification email
                </button>
              </p>
              <Link 
                to="/login" 
                className="text-sm text-primary-700 hover:text-primary-800 font-medium"
              >
                ← Back to login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900">
          {isInvitationMode ? 'Complete Your Registration' : 'Create Your Account'}
        </h2>
        <p className="text-gray-600 mt-2">
          {isInvitationMode 
            ? `Welcome ${invitationData?.full_name || 'to Skillzen'}! Complete your registration to get started.`
            : 'Join thousands of learners on their journey to success'
          }
        </p>
        {isInvitationMode && invitationData && (
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-blue-600" />
              <div className="text-sm">
                <p className="text-blue-800 font-medium">
                  Invited to: {invitationData.tenant_name || 'Skillzen'}
                </p>
                <p className="text-blue-600">
                  Email: {invitationData.email}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Name Fields */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                {...register('given_name')}
                type="text"
                className={`input-field pl-10 ${isInvitationMode ? 'bg-gray-50' : ''}`}
                placeholder="John"
                autoComplete="off"
              />
            </div>
            {errors.given_name && <p className="text-red-500 text-xs mt-1">{errors.given_name.message}</p>}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                {...register('family_name')}
                type="text"
                className={`input-field pl-10 ${isInvitationMode ? 'bg-gray-50' : ''}`}
                placeholder="Doe"
                autoComplete="off"
              />
            </div>
            {errors.family_name && <p className="text-red-500 text-xs mt-1">{errors.family_name.message}</p>}
          </div>
        </div>

        {/* Email Field */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              {...register('email')}
              type="email"
              className={`input-field pl-10 pr-10 ${
                emailAvailable === false ? 'border-red-300' :
                emailAvailable === true ? 'border-green-300' : ''
              } ${isInvitationMode ? 'bg-gray-50' : ''}`}
              placeholder="john.doe@example.com"
              disabled={isInvitationMode}
              autoComplete="off"
            />
            {checkingEmail && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            {!checkingEmail && emailAvailable === true && email && (
              <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
            )}
            {!checkingEmail && emailAvailable === false && (
              <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-500" />
            )}
          </div>
          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
          {!errors.email && emailAvailable === false && (
            <p className="text-red-500 text-xs mt-1">This email is already registered</p>
          )}
        </div>

        {/* Phone Field */}
        <div>
          <Controller
            name="mobile_number"
            control={control}
            rules={{
              validate: (value) => {
                // Skip validation if empty (optional field)
                if (!value || value.trim() === '') return true;
                // Get digits only to check if user has started entering
                const digitsOnly = value?.replace(/\D/g, '') || '';
                // If very few digits (likely just country code), skip validation
                if (digitsOnly.length <= 3) return true;
                // Use library's built-in validation for proper country-specific validation
                return isValidPhoneNumber(value) || 'Please enter a valid mobile number';
              }
            }}
            render={({ field }) => (
              <InputField
                label="Phone Number (Optional)"
                fieldType="phone"
                error={errors.mobile_number?.message}
                phoneValue={field.value || ''}
                onPhoneChange={(phone) => field.onChange(phone)}
                phoneCountry="in"
              />
            )}
          />
        </div>

        {/* Employee ID Field */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Employee ID (Optional)</label>
          <input
            {...register('employee_id')}
            type="text"
            className="input-field"
            placeholder="Enter your employee ID"
            autoComplete="off"
          />
          {errors.employee_id && <p className="text-red-500 text-xs mt-1">{errors.employee_id.message}</p>}
        </div>

        {/* Password Field */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              {...register('password')}
              type={showPassword ? 'text' : 'password'}
              className="input-field pl-10 pr-10"
              placeholder="Enter a strong password"
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              tabIndex={-1}
            >
              {showPassword ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
            </button>
          </div>
          {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>}
          <PasswordStrengthIndicator 
            password={password || ''} 
            onStrengthChange={setPasswordStrength}
          />
        </div>

        {/* Confirm Password Field */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password</label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              {...register('confirm_password')}
              type={showConfirmPassword ? 'text' : 'password'}
              className="input-field pl-10 pr-10"
              placeholder="Re-enter your password"
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              tabIndex={-1}
            >
              {showConfirmPassword ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
            </button>
          </div>
          {errors.confirm_password && <p className="text-red-500 text-xs mt-1">{errors.confirm_password.message}</p>}
        </div>

        {/* Optional Fields */}
        {/* <div className="space-y-4 pt-2">
          <h3 className="text-sm font-medium text-gray-700">Additional Information <span className="text-gray-400">(Optional)</span></h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  {...register('company')}
                  type="text"
                  className="input-field pl-10"
                  placeholder="Company Name"
                  autoComplete="off"
                />
              </div>
            </div>
            
            <div>
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  {...register('job_title')}
                  type="text"
                  className="input-field pl-10"
                  placeholder="Job Title"
                  autoComplete="off"
                />
              </div>
            </div>
          </div>

          <div>
            <input
              {...register('referral_code')}
              type="text"
              className="input-field"
              placeholder="Referral Code (if you have one)"
              autoComplete="off"
            />
          </div>
        </div> */}

        {/* Submit Button */}
        <button
          type="submit"
          // disabled={isSubmitting || registrationMutation.isPending || (emailAvailable === false && !isInvitationMode) || passwordStrength === 'weak'}
          className="btn-primary w-full flex items-center justify-center gap-2"
        >
          {(isSubmitting || registrationMutation.isPending) ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Creating Account...
            </>
          ) : (
            <>
              Create Account
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>

        <div className="text-center text-sm text-gray-600">
          Already have an account?{' '}
          <Link 
            to="/login" 
            className="text-primary-700 hover:text-primary-800 font-medium"
          >
            Sign in here
          </Link>
        </div>
      </form>
    </div>
  );
}